/**
 * Storefront Currency Context
 * Provides currency display preferences for storefront customers.
 * Allows switching between store's local currency and USD.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from '../axiosConfig';

const StorefrontCurrencyContext = createContext();

// Supported currencies with their metadata
const CURRENCIES = {
  NGN: { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', decimalPlaces: 2 },
  GHS: { code: 'GHS', symbol: 'GH₵', name: 'Ghanaian Cedi', decimalPlaces: 2 },
  KES: { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling', decimalPlaces: 2 },
  TZS: { code: 'TZS', symbol: 'TSh', name: 'Tanzanian Shilling', decimalPlaces: 0 },
  UGX: { code: 'UGX', symbol: 'USh', name: 'Ugandan Shilling', decimalPlaces: 0 },
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', decimalPlaces: 2 },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', decimalPlaces: 2 },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', decimalPlaces: 2 }
};

// Storage key for customer currency preference
const CURRENCY_STORAGE_KEY = 'revstar_storefront_currency';

export const useStorefrontCurrency = () => {
  const context = useContext(StorefrontCurrencyContext);
  if (!context) {
    // Return default values if not within provider (graceful degradation)
    return {
      storeCurrency: 'NGN',
      displayCurrency: 'NGN',
      exchangeRates: { NGN: 1 },
      setDisplayCurrency: () => {},
      formatPrice: (amount) => `₦${Number(amount || 0).toLocaleString()}`,
      convertPrice: (amount) => amount,
      getCurrencyInfo: () => CURRENCIES.NGN,
      getAvailableCurrencies: () => [CURRENCIES.NGN, CURRENCIES.USD],
      loading: false
    };
  }
  return context;
};

export const StorefrontCurrencyProvider = ({ children, store }) => {
  // Store's base currency (from store settings)
  const storeCurrency = store?.currency || store?.settings?.currency || 'NGN';

  // Customer's display preference (stored in localStorage)
  const [displayCurrency, setDisplayCurrencyState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(CURRENCY_STORAGE_KEY);
      // Validate saved currency is either store currency or USD
      if (saved && (saved === storeCurrency || saved === 'USD')) {
        return saved;
      }
    }
    return storeCurrency;
  });

  const [exchangeRates, setExchangeRates] = useState({ NGN: 1 });
  const [loading, setLoading] = useState(true);

  // Fetch exchange rates from backend (public endpoint)
  useEffect(() => {
    const fetchRates = async () => {
      try {
        console.log('[StorefrontCurrency] Fetching exchange rates...');
        const response = await axios.get('/currency/rates');
        console.log('[StorefrontCurrency] Rates response:', response.data);
        if (response.data?.success && response.data?.rates) {
          setExchangeRates(response.data.rates);
          console.log('[StorefrontCurrency] Exchange rates loaded:', response.data.rates);
        }
      } catch (error) {
        console.error('[StorefrontCurrency] Error fetching rates:', error);
        // Keep default rates { NGN: 1 } - backend will provide rates when available
      } finally {
        setLoading(false);
      }
    };

    fetchRates();
  }, []);

  // Update display currency when store currency changes
  useEffect(() => {
    // If current display currency is not valid for this store, reset to store currency
    if (displayCurrency !== storeCurrency && displayCurrency !== 'USD') {
      setDisplayCurrencyState(storeCurrency);
    }
  }, [storeCurrency, displayCurrency]);

  /**
   * Set the display currency preference
   */
  const setDisplayCurrency = useCallback((currency) => {
    // Only allow store currency or USD
    if (currency !== storeCurrency && currency !== 'USD') {
      return;
    }
    setDisplayCurrencyState(currency);
    if (typeof window !== 'undefined') {
      localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
    }
  }, [storeCurrency]);

  /**
   * Convert price from store currency to display currency
   */
  const convertPrice = useCallback((amount, fromCurrency = null) => {
    const source = fromCurrency || storeCurrency;
    if (!amount || isNaN(amount)) return 0;
    if (source === displayCurrency) return parseFloat(amount);

    const fromRate = exchangeRates[source] || 1;
    const toRate = exchangeRates[displayCurrency] || 1;

    // Convert through NGN as base
    const amountInNGN = parseFloat(amount) / fromRate;
    const converted = amountInNGN * toRate;

    console.log('[StorefrontCurrency] Converting:', {
      amount,
      from: source,
      to: displayCurrency,
      fromRate,
      toRate,
      amountInNGN,
      converted
    });

    // Round based on target currency decimals
    const decimals = CURRENCIES[displayCurrency]?.decimalPlaces ?? 2;
    return Math.round(converted * Math.pow(10, decimals)) / Math.pow(10, decimals);
  }, [storeCurrency, displayCurrency, exchangeRates]);

  /**
   * Format price with currency symbol
   */
  const formatPrice = useCallback((amount, options = {}) => {
    const {
      currency = displayCurrency,
      convert = true,
      showCode = false
    } = options;

    const currencyInfo = CURRENCIES[currency] || CURRENCIES.NGN;

    // Convert if needed
    let value = parseFloat(amount || 0);
    const shouldConvert = convert && currency === displayCurrency && storeCurrency !== displayCurrency;

    if (shouldConvert) {
      console.log('[StorefrontCurrency] formatPrice converting:', { amount, storeCurrency, displayCurrency });
      value = convertPrice(amount, storeCurrency);
    }

    // Format number
    const formattedAmount = value.toLocaleString('en-US', {
      minimumFractionDigits: currencyInfo.decimalPlaces,
      maximumFractionDigits: currencyInfo.decimalPlaces
    });

    const result = `${currencyInfo.symbol}${formattedAmount}`;
    return showCode ? `${result} ${currencyInfo.code}` : result;
  }, [displayCurrency, storeCurrency, convertPrice]);

  /**
   * Format price showing both currencies
   */
  const formatDualPrice = useCallback((amount) => {
    if (storeCurrency === displayCurrency) {
      return formatPrice(amount, { convert: false });
    }

    const originalFormatted = formatPrice(amount, { currency: storeCurrency, convert: false });
    const convertedFormatted = formatPrice(amount, { currency: displayCurrency, convert: true });

    return `${originalFormatted} (~${convertedFormatted})`;
  }, [storeCurrency, displayCurrency, formatPrice]);

  /**
   * Get currency info
   */
  const getCurrencyInfo = useCallback((currency) => {
    return CURRENCIES[currency] || null;
  }, []);

  /**
   * Get available currencies for this storefront
   * Returns store currency + USD
   */
  const getAvailableCurrencies = useCallback(() => {
    const currencies = [
      CURRENCIES[storeCurrency] || CURRENCIES.NGN
    ];

    // Add USD if not already the store currency
    if (storeCurrency !== 'USD') {
      currencies.push(CURRENCIES.USD);
    }

    return currencies;
  }, [storeCurrency]);

  /**
   * Get the current exchange rate from store currency to display currency
   */
  const getCurrentExchangeRate = useCallback(() => {
    if (storeCurrency === displayCurrency) return 1;

    const fromRate = exchangeRates[storeCurrency] || 1;
    const toRate = exchangeRates[displayCurrency] || 1;

    return toRate / fromRate;
  }, [storeCurrency, displayCurrency, exchangeRates]);

  const value = {
    // State
    storeCurrency,
    displayCurrency,
    exchangeRates,
    loading,

    // Functions
    setDisplayCurrency,
    convertPrice,
    formatPrice,
    formatDualPrice,
    getCurrencyInfo,
    getAvailableCurrencies,
    getCurrentExchangeRate,

    // Constants
    CURRENCIES
  };

  return (
    <StorefrontCurrencyContext.Provider value={value}>
      {children}
    </StorefrontCurrencyContext.Provider>
  );
};

export default StorefrontCurrencyContext;
